// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import 'phoenix_html'
import 'ninja-keys'
import posthog from 'posthog-js'
import reconnectAfterMs from './utils/reconnect-after-ms'

posthog.init('phc_nZZ9aj2xwkzCG4F3IZ6mQ3eOT6zOQu9Ca8VQ6NMED2A', { api_host: 'https://us.i.posthog.com' })

// Establish Phoenix Socket and LiveView configuration.
import { Socket } from 'phoenix'
import { LiveSocket } from 'phoenix_live_view'

// import initFrontend from './frontend/init'
import { appsignal } from './appsignal'
import SelectEvents from './hooks/SelectEvents.ts'
import SelectListBox from './hooks/SelectListBox.ts'
import FloatingComboBox from './hooks/FloatingComboBox.ts'
import PetalFrameworkHooks from '../../deps/petal_framework/assets/js/hooks'
import LiveNotetakerHook from './liveNotetaker/hook'
import { WebRecorder2Hook, WebRecorder2MeetingDetailHook } from './webRecorder2/hook'
import { ConfirmDeleteMeetingHook } from './hooks/ConfirmDeleteMeeting'
import PlaybackSyncHook from './hooks/playback-sync'
import Sortable from './hooks/sortable'
import IndeterminateCheckbox from './hooks/IndeterminateCheckbox'
import CursorOnFocus from './hooks/cursor-on-focus'
import DatePickerHook from './hooks/date-picker'
import DateTimePickerHook from './hooks/datetime-picker'
import ProxyHook from './hooks/proxy'
import ContentEditableInputHook from './hooks/contenteditable-input'
import MagicTyper from './hooks/magic-typer'
import AutoTitle from './hooks/auto-title.js'
import DropZone from './hooks/drop-zone'
import FocusFirst from './hooks/focus-first'
import ClearableInput from './hooks/clearable-input'
import CopyHtml from './hooks/copy-html'
import RichTextCopy from './hooks/rich-text-copy'
import CurrencyFormatter from './hooks/currency-formatter'
import GoogleTagManager from './hooks/google-tag-manager'
import SelectTextTooltip from './hooks/select-text-tooltip'
import AutoPlayAtTimestamp from './hooks/auto-play-at-timestamp'
import PreventTextSelection from './hooks/prevent-text-selection'
import ClearFlash from './hooks/clear-flash'
import Phone from './hooks/phone'
import ToastsHook from './toasts/hook'
import startUrlWatcher from './utils/url-watcher.js'
import PhoenixCustomEvent from 'phoenix-custom-event-hook'
import S3 from './uploaders/s3'
import RestrictInput from './hooks/restrict-input'
import truncateChar from './utils/truncate-char'
import DisableOnClick from './hooks/disable-on-click'
import AvatarLoader from './hooks/avatar-loader'
import AutoGrowTextarea from './hooks/auto-grow-textarea'
import ComboboxRefocusHook from './hooks/combobox-refocus.js'
import MermaidHook from './hooks/mermaid.js'
import MermaidPanZoom from './hooks/mermaid_pan_zoom'
import ReCaptcha from './hooks/recaptcha'
import JumpComboBoxHook from './hooks/combo_box'
import AISourcePopover from './hooks/ai-source-popover'
import GraphViz from './hooks/graphviz'
import EnterKeySubmitModal from './hooks/enter-key-submit-modal'
import ExportOnce from './hooks/export_once_hook.js'
import ClickOutside from './hooks/click-outside'
import SegmentedControlTabs from './hooks/segmented-control-tabs.ts'
import FormBuilderContainer from './hooks/form-builder-container.js'
// charts
import ChartHooks from './hooks/charts'
import LocalStorage from './hooks/local-storage.js'

import { clearCompletedRecordingStorage } from './webRecorder2/recordingStorage'
import * as GlobalPubSub from './pubSub'

// custom web components
import './web-components/rich-text-editor-with-search'
import './web-components/avatar'
import EnterKeySubmit from './hooks/enter-key-submit'
import ScrollToBottom from './hooks/scroll-to-bottom.js'
import InfiniteScroll from './hooks/infinite-scroll.js'
import ScrollManager from './hooks/scroll-manager.js'
import { customErrorHandler } from './utils/console'

// Dev Tool hooks
import DevToolHooks from './hooks/dev_tool'

async function initialize() {
  let csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content')
  let url = '/live'
  if (window.location.pathname.startsWith('/admin')) {
    url = '/admin/live'
  }

  let zoomSdk = window.zoomSdk || null

  let params = {
    _csrf_token: csrfToken,
    locale: Intl.NumberFormat().resolvedOptions().locale,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: -(new Date().getTimezoneOffset() / 60),
    user_agent: navigator.userAgent,
  }

  if (zoomSdk) {
    const configResponse = await zoomSdk.config({
      version: '0.16',
      popoutSize: { width: 480, height: 360 },
      capabilities: ['getUserContext'],
    })

    const user = await zoomSdk.getUserContext()

    const zoom_in_client_app = {
      user: user,
      config_response: configResponse,
    }

    params = { ...params, zoom_in_client_app }
  }

  let liveSocket = new LiveSocket(url, Socket, {
    hooks: {
      SelectEvents,
      SelectListBox,
      DateTimePickerHook,
      DatePickerHook,
      FloatingComboBox,
      LiveNotetakerHook,
      WebRecorder2Hook,
      WebRecorder2MeetingDetailHook,
      ConfirmDeleteMeetingHook,
      PlaybackSyncHook,
      Sortable,
      IndeterminateCheckbox,
      CursorOnFocus,
      ProxyHook,
      ContentEditableInputHook,
      MagicTyper,
      AutoTitle,
      DropZone,
      FocusFirst,
      ClearableInput,
      CopyHtml,
      RichTextCopy,
      CurrencyFormatter,
      GoogleTagManager,
      PhoenixCustomEvent,
      SelectTextTooltip,
      AISourcePopover,
      AutoPlayAtTimestamp,
      EnterKeySubmit,
      EnterKeySubmitModal,
      ScrollToBottom,
      ScrollManager,
      InfiniteScroll,
      Phone,
      ClearFlash,
      ToastsHook,
      PreventTextSelection,
      RestrictInput,
      DisableOnClick,
      AvatarLoader,
      AutoGrowTextarea,
      ComboboxRefocusHook,
      MermaidHook,
      MermaidPanZoom,
      ReCaptcha,
      JumpComboBoxHook,
      ExportOnce,
      GraphViz,
      ClickOutside,
      SegmentedControlTabs,
      FormBuilderContainer,
      LocalStorage,
      ...ChartHooks,
      ...PetalFrameworkHooks,
      ...DevToolHooks,
    },
    reconnectAfterMs,
    uploaders: { S3: S3 },
    dom: {
      onBeforeElUpdated(from, to) {
        if (from._x_dataStack) {
          window.Alpine.clone(from, to)
        }
      },
    },
    viewLogger: (view, kind, msg, obj) => {
      if (['join', 'destroyed', 'error', 'timeout', 'hook'].includes(kind)) {
        console.log(`[LiveView ${kind}] ${msg}\nAffected object:`, obj, '\nView:', view)
      }
    },
    params,
  })

  liveSocket.socket.onClose(event => {
    if (event.code === 1000) {
      // Though the code 1000 means a normal closure
      // (https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code),
      // in the context of a Phoenix LiveView app normal closures shouldn't happen that often.
      //
      // In this case it indicates that the client closed the connection to the server
      // (rather than the server closing it with the client).
      //
      // We're using it as a fuzzy heuristic for full-page reloads.
      appsignal.sendError(new Error('Potential full-page refresh occurred'), span => {
        span.setAction('Full-page refresh')
        span.setTags({ reason: event.reason })
      })
    }
  })

  liveSocket.socket.onError(error => {
    appsignal.sendError(new Error('WebSocket error occurred'), span => {
      span.setAction('WebSocket Error')
      span.setTags({
        message: error.message,
      })
    })
  })

  // Show progress bar on live navigation and form submits
  // topbar.config({ barColors: { 0: '#60781B' }, shadowColor: 'rgba(0, 0, 0, .3)' })
  // window.addEventListener('phx:page-loading-start', (info) => topbar.delayedShow(200))
  // window.addEventListener('phx:page-loading-stop', (info) => topbar.hide())

  // connect if there are any LiveViews on the page
  liveSocket.connect()

  // expose liveSocket on window for web console debug logs and latency simulation:
  // >> liveSocket.enableDebug()
  // >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
  // >> liveSocket.disableLatencySim()
  window.liveSocket = liveSocket

  window.simulateDeploy = function() {
    window.liveSocket.disconnect(() => {
      window.liveSocket.connect()
    })
  }

  // Connect to the connected channel to help track
  // the total number of websocket connections that have been made.
  liveSocket.getSocket().channel('connected').join()

  window.addEventListener('phx:copy', (event) => {
    navigator.clipboard.writeText(event.detail.text).then(() => {
      console.log('Copied')
    })
  })

  window.addEventListener('phx:clear_saved_input', (event) => {
    Object.keys(localStorage).forEach((key) => {
      if (key.includes('saved-value')) {
        localStorage.removeItem(key)
      }
    })
  })

  window.addEventListener('phx:rich_copy', (event) => {
    const htmlContent = event.detail.text
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const clipboardItem = new ClipboardItem({ 'text/html': blob })
    navigator.clipboard
      .write([clipboardItem])
      .then(() => {
        console.log('Rich text copied successfully!')
        // You can add a nice tooltip or other UI feedback here
      })
      .catch((error) => {
        console.error('Failed to copy rich text:', error)
      })
  })

  window.addEventListener('phx:download-csv', (event) => {
    downloadFile(event, 'text/csv')
  })


  window.addEventListener('phx:open_blank', (event) => {
    window.open(event.detail.url, '_blank')
  })

  window.addEventListener("phx:download-file", (event) => {
    const { filetype } = event.detail
    console.log("filetype")
    console.log(filetype)
    downloadFile(event, filetype)
  });

  function downloadFile(event, type) {
    const { content, filename } = event.detail
    // Decode the URL-safe base64 string
    const binaryString = atob(content.replace(/-/g, '+').replace(/_/g, '/'))
    // Convert binary string to Uint8Array
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const blob = new Blob([bytes], { type: type })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const sharedTomSelectOptions = {
    no_results: function(data) {
      return (
        '<div class="no-results">No results found for <span class="font-medium">' +
        escapeOutput(data.input) +
        '</span></div>'
      )
    },
    loading: function(data) {
      return '<div class="flex items-center justify-center px-5 py-5"><img class="w-6 h-6" src="/images/icon-jump-animated.svg" /></div>'
    },
  }

  window.multipleSelectClearSearchText = {
    onItemAdd: function() {
      this.setTextboxValue('')
      this.refreshOptions()
    },
  }

  window.basicTomSelectOptions = {
    preload: true,
    render: {
      ...sharedTomSelectOptions,
    },
  }

  window.subtitleComboBoxOptions = {
    maxOptions: null,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        let detail = ''
        if (json.detail) {
          detail = `<div style="color: gray; font-size: 12px;">${escapeOutput(json.detail) || ''}</div>`
        }

        return `
        <div>
          <div>${escapeOutput(json.title)}</div>
          <div style="color: gray; font-size: 12px;">${escapeOutput(json.subtitle) || ''}</div>
          ${detail}
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        if (!json.subtitle) return `<div>${json.title}</div>`
        return `<div>${json.title} | ${json.subtitle}</div>`
      },
      ...sharedTomSelectOptions,
    },
  }

  window.intRefComboBoxOptions = {
    maxOptions: null,
    preload: true,
    loadThrottle: 700,
    onInitialize: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      if (proxy) {
        this.open()
      }
    },
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        return `
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 8px;">
          <div class="flex-1 ml-2">
            <span>${escapeOutput(json.title)}</span>
            <div style="color: gray; font-size: 14px; margin-top: 2px;">${escapeOutput(json.subtitle) || ''}</div>
          </div>
          <span style="padding: 6px 12px; background-color: #DFE4E7; border-radius: 20px; font-size: 12px; font-weight: 600;">${json.type}</span>
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        if (json.subtitle) {
          return `<div>${escapeOutput(json.title)} | ${escapeOutput(json.subtitle) || ''}</div>`
        } else {
          return `<div>${escapeOutput(json.title)}</div>`
        }
      },
      ...sharedTomSelectOptions,
    },
  }

  window.generateMeetingPrepIntRefComboBoxOptions = {
    ...intRefComboBoxOptions,
    onChange: function(value, data) {
      const form = this.input.closest('form')
      const button = form.querySelector('button[type="submit"]')
      const disabled = value.length == 0
      button.disabled = disabled
      if (disabled) {
        button.classList.add('pc-button--disabled')
      } else {
        button.classList.remove('pc-button--disabled')
      }
    },
    onItemAdd: function() {
      this.setTextboxValue('')
      this.refreshOptions()
    },
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        return `
        <div style="display: flex; align-items: start; justify-content: space-between; padding: 8px;">
          <div style="flex-grow: 3">
            <span>${escapeOutput(json.title)}</span>
            <div style="color: gray; font-size: 14px; margin-top: 2px;">${escapeOutput(json.subtitle) || ''}</div>
          </div>
          <span style="padding: 6px 12px; background-color: #DFE4E7; border-radius: 20px; font-size: 12px; font-weight: 600;">${json.type}</span>
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return `<div>${escapeOutput(json.title)} | ${escapeOutput(json.subtitle) || ''}</div>`
      },
      ...sharedTomSelectOptions,
    },
  }

  const taskStatusOption = (json) => {
    const comboBoxElement = document.querySelector('#truncate-selected')
    const parentWidth = comboBoxElement.parentElement.offsetWidth
    const maxLength = parentWidth / 14

    return `
        <div class="flex items-center font-medium gap-x-1">
          <div class="w-3 h-3 rounded-full" style="background-color: ${json.color};"></div>
          <div>${truncateChar(escapeOutput(json.name), maxLength)}</div>
        </div>
        `
  }

  window.taskStatusComboBoxOptions = {
    maxOptions: null,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        return taskStatusOption(json)
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return taskStatusOption(json)
      },
      ...sharedTomSelectOptions,
    },
  }

  const userOption = (json) => {
    return `
        <div class="flex">
          <div class="flex items-center whitespace-nowrap rounded-full font-medium gap-x-1 bg-tertiary h-7 pl-1 pr-3 text-sm">
            <div class="w-5 h-5 rounded-full text-primary-inverse text-[10px] flex items-center justify-center" style="background-color: ${json.color};">
              ${json.initials}
            </div>
            <div>${escapeOutput(json.name)}</div>
          </div>
        </div>
        `
  }
  window.userComboBoxOptions = {
    maxOptions: null,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        return userOption(json)
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return userOption(json)
      },
      ...sharedTomSelectOptions,
    },
  }

  const assignableOption = (json) => {
    let sources = [...new Set(json.sources)].map((source) => `<img src="/images/logo-source-${source}.svg" class="h-5" />`).join(' ')

    return `
        <div class="flex items-center justify-between">
          <div class="flex items-center whitespace-nowrap gap-x-2 h-7 pl-1 pr-3">
            <div class="w-5 h-5 rounded-full text-primary-inverse text-[10px] flex items-center justify-center" style="background-color: ${json.color};">
              ${json.initials}
            </div>
            <div>${escapeOutput(json.title)}</div>
          </div>
          <div class="flex items-center gap-x-2">
            ${sources}
          </div>
        </div>
        `
  }

  window.taskUserComboBoxOptions = {
    maxOptions: null,
    // preload: true,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        return assignableOption(json)
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return assignableOption(json)
      },
      ...sharedTomSelectOptions,
    },
    onInitialize: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      if (this.input.dataset.autoOpen) {
        this.open()
      }
      this.on('blur', () => {
        if (this.input.dataset.change) {
          proxy.proxyPushEventTo(this.input.dataset.change, { value: this.getValue() })
        }
      })
    },
    onChange: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      if (this.getValue() && this.input.dataset.change) {
        proxy.proxyPushEventTo(this.input.dataset.change, { value: this.getValue() })
      }
    },
  }

  window.schemaComboBoxOptions = {
    maxOptions: null,
    render: {
      ...sharedTomSelectOptions,
    },
    onInitialize: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      this.open()
      /**
       * HACK: 🤡 @blakedietz 2025-02-20 - in certain situations when the combobox is inside
       * of a container that can scroll horizontally, if the combobox is clicked the scroll
       * position is reset. Unfortunately I've spent enough time trying to find the root cause
       * to no avail. So instead of a true fix, I offer you this ugly bandaid 🩹.
       * Assume that places where `data-scroll-into-view="true"` is set are parts of the product
       * that have been reported to be impacted by the bug. These usages are by no means
       * exhaustive and there may be more.
       */
      this.on('focus', () => {
        if (this.input.dataset.scrollIntoView == 'true') {
          this.input.scrollIntoView({ inline: 'start' })
        }
      })
      this.on('blur', () => {
        if (this.input.dataset.blur) {
          proxy.proxyPushEventTo(this.input.dataset.blur, { value: this.getValue() })
        }
      })
    },
  }

  const sourceOption = (data) => {
    const json = JSON.parse(data.text)
    let sources = json.sources.map((source) => `<img src="/images/logo-source-${source}.svg" />`)

    return `
        <div class="flex items-center">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center whitespace-nowrap gap-x-2 h-7 pl-1 pr-3">
              ${escapeOutput(json.title)}
            </div>
            <div class="flex items-center gap-x-1">
              ${sources}
            </div>
          </div>
        </div>
        `
  }

  window.sourceComboBoxOptions = {
    maxOptions: null,
    render: {
      option: sourceOption,
      item: sourceOption,
      ...sharedTomSelectOptions,
    },
  }

  window.schemaTypeComboBoxOptions = {
    maxOptions: 500,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        return `
        <div class="flex justify-between">
          <div>${escapeOutput(json.title)}</div>
          <div style="color: gray; font-size: 12px;">${escapeOutput(json.subtitle) || ''}</div>
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        if (json.title !== json.subtitle) {
          return `<div>${escapeOutput(json.title)} | ${escapeOutput(json.subtitle)}</div>`
        } else {
          return `<div>${escapeOutput(json.title)}</div>`
        }
      },
      ...sharedTomSelectOptions,
    },
  }

  window.checkboxTomSelectOptions = {
    render: {
      option: function(data, escape) {
        let tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.wrapper.innerHTML

        let selectedValue = tempDiv.querySelector('.ts-control div.item')?.getAttribute('data-value')
        let isChecked = selectedValue === data.value ? 'checked' : ''

        const whiteTickPath = '/images/tick.svg'

        // If you use the `tom_select_options` to set the options, as we do in
        // `JumpWeb.Components.Suggestion`, `data` will be a JavaScript object, and you must
        // provide the `title` and `value` at minimum (optionally `disabled` and `detail`).
        // On the other hand, if you set the options via the `options` attribute on the
        // `<.combo_box>`, as in `JumpWeb.Components.MicrosoftTeamsSettings`, `data.text` will
        // be the name of the option.
        const json =
          typeof data === 'object' && data && data.title
            ? data
            : data.text.startsWith('{')
              ? JSON.parse(data.text)
              : { title: data.text }

        const isDisabled = !!json.disabled

        return `
          <div
            id=${this.input.id}
            class="flex items-center justify-between gap-2 px-4 py-2 rounded-sm hover:bg-sky-50 ${isChecked ? 'bg-sky-50' : ''} ${isDisabled ? '!opacity-50 pointer-events-none' : ''}"
            ${isDisabled ? 'disabled="true"' : ''}
          >
            <div class="flex flex-col">
              <div class="text-primary text-base">${escapeOutput(json.title)}</div>
              <div class="text-secondary text-sm">${escapeOutput(json.detail || '')}</div>
            </div>
            <div class="checkmark w-5 h-5 flex shrink-0 items-center justify-center border rounded-full ${isChecked ? 'bg-black' : 'border-gray-300'}">
              ${isChecked ? `<img src="${whiteTickPath}" class="w-3 h-3" />` : ''}
            </div>
          </div>
        `
      },
      item: function(data, escape) {
        const json =
          typeof data === 'object' && data && data.title
            ? data
            : data.text.startsWith('{')
              ? JSON.parse(data.text)
              : { title: data.text }
        return `<div>${escapeOutput(json.title)}</div>`
      },
      ...sharedTomSelectOptions,
    },
    onChange: function(selectedValue) {
      let dropdown = document.getElementById(this.input.id + '-ts-dropdown')

      dropdown.querySelectorAll(':scope > div').forEach((option) => {
        let value = option.getAttribute('data-value')
        let checkmark = option.querySelector('.checkmark')

        const whiteTickPath = '/images/tick.svg'

        if (value === selectedValue) {
          option.classList.add('bg-sky-50')
          if (checkmark) {
            checkmark.innerHTML = `<img src="${whiteTickPath}" class="w-3 h-3" />`
            checkmark.classList.add('bg-black')
          }
        } else {
          option.classList.remove('bg-sky-50')
          if (checkmark) {
            checkmark.innerHTML = ''
            checkmark.classList.remove('bg-black')
          }
        }
      })
    },
  }

  setTimeout(() => {
    if (
      location.pathname != '/login' &&
      // Don't make additional requests to the backend in E2E tests
      !navigator.userAgent.includes('BeamMetadata')
    ) {
      startUrlWatcher()
    }
  }, 1000)

  window.posthog = posthog

  function escapeOutput(toOutput) {
    if (toOutput) {
      return toOutput
        .replace(/\&/g, '&amp;')
        .replace(/\</g, '&lt;')
        .replace(/\>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/\'/g, '&#x27;')
        .replace(/\//g, '&#x2F;')
    } else {
      return ''
    }
  }

  function formatEmail(email) {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    return `<span class="text-zinc-950 underline">${escapeOutput(localPart)}</span><span>@</span><span class="text-zinc-950 underline">${escapeOutput(domain)}</span>`;
  }

  const CONFIRM_ATTRIBUTE = 'data-confirm-fired'
  const DANGER_DIALOG = document.getElementById('danger_dialog')
  const MODAL_WRAPPER = document.getElementById('modal_wrapper')

  document.body.addEventListener(
    'phoenix.link.click',
    function(event) {
      event.stopPropagation()
      const { target: targetButton } = event
      const confirmationText = targetButton.getAttribute('data-confirm')
      const confirmButtonLabel = targetButton.getAttribute('data-confirm-button-label') || 'Yes, delete'
      const requireInput = targetButton.getAttribute('data-require-input')

      if (!confirmationText) {
        return true
      }

      if (targetButton.hasAttribute(CONFIRM_ATTRIBUTE)) {
        targetButton.removeAttribute(CONFIRM_ATTRIBUTE)
        return true
      }

      event.preventDefault()
      targetButton.setAttribute(CONFIRM_ATTRIBUTE, '')

      const openDropdown = document.querySelector('.pc-dropdown__menu-items-wrapper[style*="display: block"]')
      if (openDropdown) {
        openDropdown.style.display = 'none'
      }

      DANGER_DIALOG.returnValue = 'cancel'
      DANGER_DIALOG.querySelector("[data-ref='body']").innerText = confirmationText
      DANGER_DIALOG.querySelector("[data-ref='confirm']").innerText = confirmButtonLabel

      const confirmInputField = DANGER_DIALOG.querySelector("[data-ref='confirm-input']")
      confirmInputField.value = ''
      if (requireInput) {
        confirmInputField.classList.remove('hidden')
        confirmInputField.setAttribute('placeholder', requireInput)
      } else {
        confirmInputField.classList.add('hidden')
      }

      MODAL_WRAPPER.classList.remove('hidden')
      DANGER_DIALOG.showModal()

      DANGER_DIALOG.addEventListener(
        'close',
        ({ target }) => {
          MODAL_WRAPPER.classList.add('hidden')
          if (target.returnValue === 'confirm') {
            const inputValue = confirmInputField.value.trim()

            if (requireInput && inputValue === '') {
              alert('Please enter the required information.')
              targetButton.removeAttribute(CONFIRM_ATTRIBUTE)
            } else {
              targetButton.setAttribute('phx-value-confirm-input', inputValue)
              targetButton.click()
            }
          } else {
            targetButton.removeAttribute(CONFIRM_ATTRIBUTE)
          }
        },
        { once: true }
      )
    },
    false
  )

  window.addEventListener(
    'phx:safe-to-clear-local-recording',
    function({ detail: { meetingId, integrationReferenceId, writerId } }) {
      clearCompletedRecordingStorage({ meetingId, integrationReferenceId, writerId: crypto.randomUUID() })
    }
  )

  window.addEventListener('phx:meeting-topic-updated', function({ detail: { meetingId, topic } }) {
    if (meetingId && typeof topic === 'string') {
      GlobalPubSub.publish('meeting-topic-updated', { meetingId, topic })
    }
  })

  window.addEventListener('phx:page-loading-stop', function(ev) {
    GlobalPubSub.publish('navigated', {})
  })

  window.addEventListener('phx:show-toast', function ({ detail: toast }) {
    GlobalPubSub.publish('show-toast', toast)
  })

  window.console.error = customErrorHandler
}

initialize()
